import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Printer,
  Edit,
  Check,
  Copy,
  FileDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { showPostSuccess, showPostError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { ProcessingModal, ProcessingStepStatus } from '@/components/shared/ProcessingModal'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { creditNotesService } from '@/services/creditNotesService'
import { financeService } from '@/services/financeService'
import { CreditNoteListItem, FinanceCustomer, ApiResponse } from '@/api/types'
import { calendarToday, calendarFromApi, addCalendarDays, formatCalendarDate } from '@/lib/calendarDate'
import { SortOrderToggle } from '@/components/shared/SortOrderToggle'
import type { ListSortDir } from '@/lib/listSort'
import { formatCurrency } from '@/lib/format'

export const CreditNotesList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [creditNotes, setCreditNotes] = useState<CreditNoteListItem[]>([])
  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortDir, setSortDir] = useState<ListSortDir>('desc')
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(undefined)
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [postingCreditNoteId, setPostingCreditNoteId] = useState<number | null>(null)
  const [showProcessingModal, setShowProcessingModal] = useState(false)
  const [processingSteps, setProcessingSteps] = useState<
    Array<{ label: string; status: ProcessingStepStatus }>
  >([
    { label: t('invoices.preparingXml'), status: 'pending' },
    { label: t('invoices.sendingToZatca'), status: 'pending' },
    { label: t('invoices.receivingQr'), status: 'pending' },
    { label: t('invoices.updatingQr'), status: 'pending' },
  ])

  const branchId = user?.selectedBranch?.branchId

  // Initialize date filters to current month
  useEffect(() => {
    const today = calendarToday()
    const [year, month] = today.split('-').map(Number)
    const firstDay = `${year}-${String(month).padStart(2, '0')}-01`
    const nextMonth = month === 12 ? `${year + 1}-01-01` : `${year}-${String(month + 1).padStart(2, '0')}-01`
    setDateFrom(firstDay)
    setDateTo(addCalendarDays(nextMonth, -1))
  }, [])

  // Load customers for filter
  useEffect(() => {
    if (!branchId) return
    const loadCustomers = async () => {
      try {
        const customerList = await financeService.getAllCustomers(branchId)
        setCustomers(customerList)
      } catch (error: any) {
        console.error('Failed to load customers', error)
      }
    }
    loadCustomers()
  }, [branchId])

  // Load credit notes
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadCreditNotes = async () => {
      try {
        setLoading(true)
        const response = await creditNotesService.getAllCreditNotes(branchId, pageNumber, pageSize, sortDir)
        const list = response.data || []
        
        console.log('=== Credit Notes List Loading ===')
        console.log('Loaded credit notes count:', list.length)
        console.log('Sample credit note (full):', JSON.stringify(list[0], null, 2))
        
        // Check all possible invoice ID field names
        const sampleCn = list[0]
        console.log('Checking invoice ID fields:', {
          refInvId: sampleCn?.refInvId,
          invoiceId: sampleCn?.invoiceId,
          refInvoiceId: sampleCn?.refInvoiceId,
          invId: sampleCn?.invId,
          invoiceNumber: sampleCn?.refInvNumber,
        })
        
        // Get all invoices once (more efficient)
        let invoices: any[] = []
        try {
          invoices = await creditNotesService.getAllInvoices(branchId)
          console.log('Loaded invoices for supply date mapping:', invoices.length)
          if (invoices.length > 0) {
            console.log('Sample invoice (full):', JSON.stringify(invoices[0], null, 2))
            console.log('Invoice supplyDate field:', invoices[0]?.supplyDate)
          }
        } catch (error) {
          console.warn('Failed to fetch invoices for supply date mapping', error)
        }
        
        // Map invoice supply dates to credit notes
        // Try matching by invoice ID first, then by invoice number
        const creditNotesWithInvoiceSupplyDate = list.map((cn: any) => {
          // Try different field names for invoice ID
          const invoiceId = cn.refInvId || cn.invoiceId || cn.refInvoiceId || cn.invId
          const invoiceNumber = cn.refInvNumber || cn.refInvoiceNumber || cn.invoiceNumber
          
          let matchedInvoice: any = null
          
          if (invoiceId && invoiceId > 0 && invoices.length > 0) {
            // Try matching by invoice ID
            matchedInvoice = invoices.find((inv: any) => {
              const invId = inv.invoiceId || inv.id
              return invId === invoiceId
            })
            
            if (matchedInvoice) {
              console.log(`Matched invoice by ID ${invoiceId}`)
            }
          }
          
          // If not matched by ID, try matching by invoice number
          if (!matchedInvoice && invoiceNumber && invoices.length > 0) {
            matchedInvoice = invoices.find((inv: any) => {
              const invNumber = inv.invoiceNumber || inv.invoiceNo || inv.invNumber
              return invNumber === invoiceNumber || invNumber === String(invoiceNumber)
            })
            
            if (matchedInvoice) {
              console.log(`Matched invoice by number ${invoiceNumber}`)
            }
          }
          
          if (matchedInvoice) {
            const supplyDate = matchedInvoice.supplyDate || ''
            console.log(`Found invoice, supply date:`, supplyDate)
            return {
              ...cn,
              invoiceSupplyDate: supplyDate,
            }
          } else {
            console.log(`Credit note ${cn.creditNoteId} - No invoice match. Invoice ID: ${invoiceId}, Invoice Number: ${invoiceNumber}`)
          }
          
          return cn
        })
        
        console.log('Credit notes with invoice supply dates:', creditNotesWithInvoiceSupplyDate.map(cn => ({
          cnId: cn.creditNoteId,
          refInvId: cn.refInvId,
          invoiceSupplyDate: cn.invoiceSupplyDate
        })))
        
        setCreditNotes(creditNotesWithInvoiceSupplyDate)
        setTotalCount(response.totalRecords || list.length || 0)
        const pages = response.totalRecords ? Math.ceil(response.totalRecords / response.pageSize) : 0
        setTotalPages(pages || 1)
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setCreditNotes([])
        setTotalCount(0)
        setTotalPages(1)
        if (!isNoData) {
          toast.error(error.response?.data?.message || 'Failed to load credit notes')
        }
      } finally {
        setLoading(false)
      }
    }

    loadCreditNotes()
  }, [branchId, pageNumber, pageSize, sortDir])

  const filteredCreditNotes = useMemo(() => {
    return creditNotes.filter((cn) => {
      // Customer filter
      if (selectedCustomerId && cn.customerId !== selectedCustomerId) {
        return false
      }

      // Date filter
      if (dateFrom && cn.creditNoteDate) {
        const cnDay = calendarFromApi(cn.creditNoteDate)
        if (cnDay && cnDay < dateFrom) return false
      }
      if (dateTo && cn.creditNoteDate) {
        const cnDay = calendarFromApi(cn.creditNoteDate)
        if (cnDay && cnDay > dateTo) return false
      }

      // Search filter
      if (!searchQuery.trim()) return true
      const query = searchQuery.toLowerCase()
      return (
        (cn.creditNoteNumber || cn.creditNoteNo || '').toLowerCase().includes(query) ||
        (cn.customerName || '').toLowerCase().includes(query) ||
        (cn.refInvNumber || '').toLowerCase().includes(query)
      )
    })
  }, [creditNotes, selectedCustomerId, searchQuery, dateFrom, dateTo])

  const formatDate = (dateString?: string) => formatCalendarDate(dateString)

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return formatCurrency(Number(amount))
  }

  const handleAddCreditNote = () => {
    navigate('/credit-notes/new')
  }

  const handleEditCreditNote = (creditNote: CreditNoteListItem) => {
    const creditNoteId = creditNote.creditNoteId ?? creditNote.id
    if (!creditNoteId) {
      toast.error('Invalid credit note ID')
      return
    }
    navigate(`/credit-notes/${creditNoteId}/edit`, { state: { creditNote } })
  }

  const handlePostCreditNote = async (creditNote: CreditNoteListItem) => {
    const creditNoteId = creditNote.creditNoteId ?? creditNote.id
    if (!creditNoteId) {
      toast.error('Invalid credit note ID')
      return
    }
    const isPosted = creditNote.isPosted === true || creditNote.isPosted === 1
    if (isPosted) {
      toast.error('Credit note already posted')
      return
    }

    // Reset steps and show modal
    setProcessingSteps([
      { label: t('invoices.preparingXml'), status: 'pending' },
      { label: t('invoices.sendingToZatca'), status: 'pending' },
      { label: t('invoices.receivingQr'), status: 'pending' },
      { label: t('invoices.updatingQr'), status: 'pending' },
    ])
    setShowProcessingModal(true)
    setPostingCreditNoteId(creditNoteId)

    try {
      // Step 1: Preparing XML
      await new Promise((resolve) => setTimeout(resolve, 800))
      setProcessingSteps((prev) =>
        prev.map((step, idx) => (idx === 0 ? { ...step, status: 'completed' as ProcessingStepStatus } : step))
      )

      // Step 2: Sending to Zatca
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setProcessingSteps((prev) =>
        prev.map((step, idx) => (idx === 1 ? { ...step, status: 'completed' as ProcessingStepStatus } : step))
      )

      // Step 3: Receiving QR
      await new Promise((resolve) => setTimeout(resolve, 800))
      setProcessingSteps((prev) =>
        prev.map((step, idx) => (idx === 2 ? { ...step, status: 'completed' as ProcessingStepStatus } : step))
      )

      // Log before making API call
      console.log('%c=== Credit Note Post Request ===', 'background: #FF5722; color: white; padding: 5px; font-weight: bold; font-size: 14px;')
      console.log('%cCredit Note ID:', 'color: #FF9800; font-weight: bold;', creditNoteId)
      console.log('%cNote: IP Address will be retrieved automatically (logged-in user IP, not server IP)', 'color: #4CAF50; font-weight: bold;')
      console.log('%c=================================', 'background: #FF5722; color: white; padding: 5px; font-weight: bold;')

      // Make API call (IP address will be retrieved automatically in the service)
      const response = await creditNotesService.postCreditNote(creditNoteId)

      // Step 4: Updating QR
      await new Promise((resolve) => setTimeout(resolve, 500))
      setProcessingSteps((prev) =>
        prev.map((step, idx) => (idx === 3 ? { ...step, status: 'completed' as ProcessingStepStatus } : step))
      )

      // Wait a bit before closing modal
      await new Promise((resolve) => setTimeout(resolve, 500))

      const message =
        (response as ApiResponse)?.message ||
        (response as ApiResponse)?.data?.message ||
        (response as any)?.msg ||
        'Credit note posted successfully'
      
      setShowProcessingModal(false)
      await showPostSuccess(message)

      // Refresh list
      if (branchId) {
        const updated = await creditNotesService.getAllCreditNotes(branchId, pageNumber, pageSize, sortDir)
        setCreditNotes(updated.data || [])
        setTotalCount(updated.totalRecords || 0)
        const pages = updated.totalRecords ? Math.ceil(updated.totalRecords / updated.pageSize) : 0
        setTotalPages(pages || 1)
      }

      setShowProcessingModal(false)
    } catch (error: any) {
      console.error('Failed to post credit note', error)
      
      // Mark current step as error
      setProcessingSteps((prev) => {
        const currentStepIndex = prev.findIndex((step) => step.status === 'pending')
        if (currentStepIndex !== -1) {
          return prev.map((step, idx) =>
            idx === currentStepIndex ? { ...step, status: 'error' as ProcessingStepStatus } : step
          )
        }
        return prev
      })

      // Wait a bit before closing modal and showing error
      await new Promise((resolve) => setTimeout(resolve, 1000))
      setShowProcessingModal(false)
      await showPostError(error)
    } finally {
      setPostingCreditNoteId(null)
    }
  }

  const handlePrint = (creditNote?: CreditNoteListItem) => {
    if (creditNote) {
      const creditNoteId = creditNote.creditNoteId ?? creditNote.id
      if (!creditNoteId) {
        toast.error('Invalid credit note ID')
        return
      }
      navigate(`/credit-notes/${creditNoteId}/print`)
    } else {
      // If no credit note provided, show message to select one
      toast.error('Please select a credit note to print')
    }
  }

  const handleCopy = () => {
    toast.success('Copy functionality coming soon')
  }

  const handleCsv = () => {
    toast.success('CSV export coming soon')
  }

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1)
  }

  const handleCustomerFilterChange = (value: string) => {
    if (value === 'all') {
      setSelectedCustomerId(undefined)
    } else {
      setSelectedCustomerId(Number(value))
    }
    setPageNumber(1)
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title="Sales Credit Notes"
          description="Manage your credit notes"
        />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title="Branch Not Selected"
              description="Please select a branch to view credit notes."
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      {/* Processing Modal */}
      <ProcessingModal open={showProcessingModal} steps={processingSteps} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sales</h1>
          <p className="text-muted-foreground">Credit Notes</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleAddCreditNote}>
            <Plus className="w-4 h-4 mr-2" />
            Add New
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Credit Note Date From</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Credit Note Date To</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Customer</label>
              <Select value={selectedCustomerId ? String(selectedCustomerId) : 'all'} onValueChange={handleCustomerFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  {customers.map(customer => (
                    <SelectItem key={customer.customerId} value={String(customer.customerId)}>
                      {customer.customerNameEn || customer.customerNameAr || `Customer ${customer.customerId}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <SortOrderToggle
                value={sortDir}
                onChange={(next) => {
                  setSortDir(next)
                  setPageNumber(1)
                }}
              />
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
              <Button variant="outline" size="sm" onClick={handleCsv}>
                <FileDown className="h-4 w-4 mr-2" />
                CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Credit Notes Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading credit notes...</p>
            </div>
          ) : filteredCreditNotes.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={searchQuery || selectedCustomerId || dateFrom || dateTo ? 'No credit notes found' : 'No credit notes yet'}
                description={
                  searchQuery || selectedCustomerId || dateFrom || dateTo
                    ? 'Try adjusting your search or filter'
                    : 'Get started by creating your first credit note.'
                }
                action={
                  !searchQuery && !selectedCustomerId && !dateFrom && !dateTo
                    ? {
                        label: 'Add New Credit Note',
                        onClick: handleAddCreditNote,
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">CN#</TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">REF INV#</TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('creditNotes.customer') || 'Customer'}</TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('creditNotes.date') || 'Date'}</TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('creditNotes.amount') || 'Amount'}</TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">Posted</TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">E-INV</TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">{t('common.edit') || 'Edit'}</TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">Post</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCreditNotes.map((creditNote) => {
                      const creditNoteId = creditNote.creditNoteId ?? creditNote.id
                      const isPosted = creditNote.isPosted === true || creditNote.isPosted === 1
                      return (
                        <TableRow key={creditNoteId} className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors">
                          <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                            {creditNote.creditNoteNumber || creditNote.creditNoteNo || `CN-${creditNoteId}`}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {(creditNote as any).refNo || creditNote.refInvNumber || '-'}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{creditNote.customerName || 'N/A'}</TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">{formatDate(creditNote.creditNoteDate)}</TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatAmount(creditNote.amount ?? creditNote.total)}
                          </TableCell>
                          <TableCell
                            className={`px-4 py-2 text-sm text-center align-middle ${
                              isPosted ? 'text-blue-800 dark:text-blue-300' : 'text-sky-500 dark:text-sky-400'
                            }`}
                          >
                            {isPosted ? 'Posted' : 'Not Posted'}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-center align-middle">
                            <div className="flex justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePrint(creditNote)}
                                title="Print Credit Note"
                                className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                              >
                                <Printer className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-2 text-center align-middle">
                            <div className="flex justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditCreditNote(creditNote)}
                                title={isPosted ? 'Credit note already posted' : 'Edit credit note'}
                                disabled={isPosted}
                                className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-2 text-center align-middle">
                            <div className="flex justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handlePostCreditNote(creditNote)}
                                disabled={isPosted || postingCreditNoteId === creditNoteId}
                                className={`h-7 w-7 p-0 ${isPosted ? 'text-green-600' : 'hover:bg-primary/10 dark:hover:bg-primary/20'}`}
                              >
                                {postingCreditNoteId === creditNoteId ? (
                                  <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></span>
                                ) : (
                                  <Check className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground">
                  Showing {(pageNumber - 1) * pageSize + 1} to{' '}
                  {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} entries
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <div className="text-sm">
                    Page {pageNumber} of {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => (prev < totalPages ? prev + 1 : prev))}
                    disabled={pageNumber >= totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

    </div>
  )
}

